import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import Grid from '../Grid';
import Text from '../Text';
import { LinearProgress } from '@mui/material';

interface IProps {
  headerItems: Omit<GridColDef, 'field'>[];
  bodyItems?: any[][];
  checkboxSelection?: boolean;
  rowHeight?: number;
  showRowCount?: boolean;
  allRowCount?: number;
  selectedRowCount?: number;
  selectedRowIds?: (string | number)[];
  rowUniqueIds?: (string | number)[];
  onSelectionChange?: (selectedRows: (string | number)[]) => void;
  isAutoRowHeight?: boolean;
  loading?: boolean;
  noRowsLabel?: string;
  headerBackgroundColor?: string;
  disableRowSelectionOnClick?: boolean;
}

const GridTag = styled(Grid)`
  height: 600px;
  overflow-y: scroll;
  border: 1px solid #e2e2e2;
  border-radius: 5px;
  overflow-y: auto;

  .MuiDataGrid-root * {
    font-size: 0.77rem;
  }
`;

export default function TableComponent(props: IProps) {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const hideableIndexs: number[] = [];

  // 외부에서 전달된 selectedRowIds와 동기화
  useEffect(() => {
    if (props.selectedRowIds !== undefined) {
      setSelectionModel(props.selectedRowIds);
    }
  }, [props.selectedRowIds]);

  const columns = useMemo(
    () =>
      props.headerItems
        .filter((item, index) => {
          // 숨길 컬럼의 index를 기록후
          // 노출할 컬럼만 filter에서 반환함
          if (item.hideable) {
            hideableIndexs.push(index);
          }
          return !item.hideable;
        })
        .map((one, index) => ({
          sortable: one.sortable ?? false,
          disableColumnMenu: true,
          ...one,
          field: `col${index}`,
        })),
    [props.headerItems],
  );

  const rows = useMemo(
    () =>
      props.bodyItems
        ? props.bodyItems.map((row, index) => {
            let rowIndex = 0;

            const rowData = row.reduce((all, current, index2) => {
              // 숨길 컬럼은 bodyItem 배열 요소에서 포함시키지 않음
              if (!hideableIndexs.includes(index2)) {
                all[`col${rowIndex}`] = current;
                rowIndex++;
              }
              return all;
            }, {} as any);

            // 고유 ID가 제공되면 그것을 사용, 없으면 인덱스 사용
            rowData.id = props.rowUniqueIds && props.rowUniqueIds[index] ? props.rowUniqueIds[index] : index;

            return rowData;
          })
        : [],
    [props.bodyItems, props.rowUniqueIds],
  );

  return (
    <>
      {props.showRowCount && (
        <Grid display="flex" justifyContent="flex-start" alignItems="center" bottom={10}>
          <Text fontSize="0.88rem" color="#666">
            {props.checkboxSelection && (props.selectedRowCount || 0) > 0
              ? `선택 ${props.selectedRowCount}건 / 총 ${props.allRowCount ? props.allRowCount : rows.length}건`
              : `총 ${props.allRowCount ? props.allRowCount : rows.length}건`}
          </Text>
        </Grid>
      )}
      <GridTag>
        <DataGrid
          columns={columns}
          rows={rows}
          hideFooter
          disableRowSelectionOnClick={props.disableRowSelectionOnClick || false}
          checkboxSelection={props.checkboxSelection}
          rowHeight={props.rowHeight || 52}
          getRowHeight={props.isAutoRowHeight ? () => 'auto' : undefined}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => {
            setSelectionModel(newSelection);
            props.onSelectionChange?.(newSelection as (string | number)[]);
          }}
          loading={props.loading}
          slots={{
            loadingOverlay: () => <LinearProgress />,
          }}
          localeText={{
            noRowsLabel: props.noRowsLabel || 'No rows',
          }}
          sx={
            props.headerBackgroundColor
              ? {
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: props.headerBackgroundColor,
                  },
                }
              : undefined
          }
        />
      </GridTag>
    </>
  );
}
